import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { LoaderSpinner } from '@/shared/ui/page-loader';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

const SSOCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signInWithCentific } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    handleSSOCallback();
  }, []);

  const handleSSOCallback = async () => {
    try {
      setStatus('processing');
      
      // Get tokens from URL parameters
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const expiresIn = searchParams.get('expires_in');
      const idToken = searchParams.get('id_token');

      if (!accessToken) {
        throw new Error('No access token received from SSO');
      }

      // Use our new backend SSO endpoint
      const { error } = await signInWithCentific(
        accessToken,
        idToken || undefined,
        refreshToken || undefined,
        expiresIn ? parseInt(expiresIn) : undefined
      );

      if (error) {
        throw new Error(`SSO login failed: ${error}`);
      }

      setStatus('success');
      
      toast({
        title: 'SSO Login Successful',
        description: 'You have been successfully logged in via SSO.',
      });

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('[SSOCallback] Error:', error);
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Unknown error occurred');
      
      toast({
        title: 'SSO Login Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    }
  };

  const handleRetry = () => {
    setStatus('processing');
    setErrorMessage('');
    handleSSOCallback();
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">
            {status === 'processing' && 'Processing SSO Login...'}
            {status === 'success' && 'SSO Login Successful!'}
            {status === 'error' && 'SSO Login Failed'}
          </CardTitle>
          <CardDescription className="text-center">
            {status === 'processing' && 'Please wait while we authenticate you...'}
            {status === 'success' && 'You will be redirected to the dashboard shortly.'}
            {status === 'error' && 'There was an issue with your SSO login.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'processing' && (
            <div className="flex justify-center">
              <LoaderSpinner />
            </div>
          )}
          
          {status === 'success' && (
            <div className="text-center">
              <div className="text-green-600 text-4xl mb-2">✓</div>
              <p className="text-sm text-gray-600">
                Redirecting to dashboard...
              </p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-red-600 text-4xl mb-2">✗</div>
                <p className="text-sm text-gray-600 mb-4">
                  {errorMessage}
                </p>
              </div>
              <div className="flex space-x-2">
                <Button onClick={handleRetry} className="flex-1">
                  Try Again
                </Button>
                <Button onClick={handleGoHome} variant="outline" className="flex-1">
                  Go Home
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SSOCallback;
